import React, { useMemo, useState } from 'react'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import {
  Box,
  PagingSidePanelType,
  RowsSkeletonLoader,
  SidePanel,
  Tabs,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { appBannersId } from 'components/app/AppBanners'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { SidePanelRowProps } from 'components/SidePanel/SidePanelSummary'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useEmbeddedCustomerAgreementsQuery } from 'hooks/queries/useEmbeddedCustomerAgreementsQuery'
import { useGetCustomerDocuments } from 'hooks/queries/useGetCustomerDocuments'
import { useGetInfoRequired } from 'hooks/queries/useGetInfoRequiredQuery'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { EmbeddedCustomerStatusHighlight } from 'pages/Customers/components/CustomerHighlight'
import { CustomerDetailsHeader } from 'pages/Customers/CustomerDetails/CustomerDetailsHeader'
import { CustomerEntityType } from 'types/customers'
import { highlightRowBasedOnId } from 'utils/table/table'
import {
  EMBEDDED_CUSTOMER_AGREEMENT_STATUSES,
  EMBEDDED_CUSTOMER_STATUSES,
} from '../helpers/constants'
import {
  DocumentRequirement,
  EmbeddedCustomer,
  EmbeddedCustomerData,
  VerificationFailure,
} from '../helpers/types'
import { getCustomerTypeLabel } from '../helpers/utils'
import { useAssociateDetails } from '../hooks/useAssociateDetails'
import { AssociateDetails } from './AssociateDetails'
import { BusinessCustomer } from './BusinessCustomer'
import { CTASection } from './CTASection'
import { CustomerChecklist } from './CustomerChecklist'
import { CustomerDocuments } from './CustomerDocuments'
import { EmbeddedCustomerAgreements } from './EmbeddedCustomerAgreements'
import { ExternalReferenceCta } from './ExternalReference/ExternalReferenceCta'
import { IndividualCustomer } from './IndividualCustomer'

export interface EmbeddedCustomerDetailsProps {
  isOpen: boolean
  onClose: () => void
  pageCustomers: PagingSidePanelType
  customerDetails: EmbeddedCustomer
  checklistBadgeCount?: string
  isReliance?: boolean
}

export const EmbeddedCustomerDetails = ({
  isOpen,
  onClose,
  pageCustomers,
  customerDetails,
  checklistBadgeCount,
  isReliance,
}: EmbeddedCustomerDetailsProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)

  const { enableCustomerTimelineTab } = useFeatureFlags()

  const { name, reference } = customerDetails || {}

  const {
    data: agreementsData = [],
    isFetching: isFetchingAgreements,
    isError: isErrorFetchingAgreements,
    refetch: refetchAgreements,
  } = useEmbeddedCustomerAgreementsQuery(reference)

  const {
    data: customerData,
    isFetching: isFetchingCustomerData,
    isError: isErrorFetchingCustomerData,
    refetch: refetchCustomerData,
  } = useCustomerQuery<EmbeddedCustomerData>(reference)

  const {
    data: documents,
    isLoading: isLoadingDocuments,
    isError: isErrorDocuments,
    refetch: refetchDocuments,
  } = useGetCustomerDocuments(customerData?.reference)

  const { data: infoRequiredData } = useGetInfoRequired(reference)

  const infoRequiredDocuments: DocumentRequirement[] =
    infoRequiredData?.infoRequired?.documents || []
  const isCompany = customerDetails?.type === CustomerEntityType.COMPANY
  const shouldShowDocumentsTab =
    !isReliance && (isCompany || !isEmpty(infoRequiredDocuments) || !isEmpty(documents?.content))

  const tabKeys = [
    ...(isCompany && enableCustomerTimelineTab ? (['Checklist'] as const) : []),
    'Summary',
    ...(shouldShowDocumentsTab ? (['Documents'] as const) : []),
  ]
  const defaultTab = isCompany && enableCustomerTimelineTab ? 'Checklist' : 'Summary'

  const [activeTab, setActiveTab] = useState(defaultTab)

  const isIndividual = customerDetails?.type === CustomerEntityType.INDIVIDUAL

  const agreementSesstionUrl = customerData?.pendingAgreementSessionUrl
  const showAgreementCTA =
    agreementSesstionUrl && customerData?.status === EMBEDDED_CUSTOMER_STATUSES.PENDING_AGREEMENTS

  const signedAgreements = agreementsData?.filter(
    ({ status }) => status === EMBEDDED_CUSTOMER_AGREEMENT_STATUSES.SIGNED
  )

  const verificationUrl = customerData?.verification?.url
  const isVerificationRequired = customerData?.status === EMBEDDED_CUSTOMER_STATUSES.INFO_REQUIRED
  const showVerificationCTA =
    verificationUrl &&
    !isReliance &&
    (isVerificationRequired || customerData?.status === EMBEDDED_CUSTOMER_STATUSES.PENDING)

  const {
    associates: associatesData,
    isLoading: isLoadingAssociates,
    isError: isErrorAssociates,
    refetch,
    pendingVerificationStatus,
  } = useAssociateDetails(reference, customerData?.company?.associates)

  const showAssociatesTab =
    isCompany && !isEmpty(associatesData) && !isLoadingAssociates && !isReliance

  const sharedCustomerDetails: SidePanelRowProps[] = useMemo(
    () => [
      {
        label: t('customerReference'),
        content: ellipseMiddleText(reference, 15, 15),
        copyText: reference,
      },
      { label: t('type'), content: getCustomerTypeLabel(customerData?.type, t) },
      ...(isReliance
        ? []
        : [
            {
              label: t('customers.externalReference'),
              content: (
                <ExternalReferenceCta
                  customerReference={reference}
                  externalReference={customerData?.externalReference}
                  refetchCustomerData={refetchCustomerData}
                />
              ),
              tooltip: t('customers.externalReferenceTooltip'),
            },
          ]),
    ],
    [customerData?.type, customerData?.externalReference, reference]
  )

  const showSummaryDetails = () => (
    <>
      {isCompany && (
        <BusinessCustomer
          customer={customerData?.company}
          sharedCustomerDetails={sharedCustomerDetails}
        />
      )}
      {isIndividual && (
        <IndividualCustomer
          customer={customerData?.individual}
          sharedCustomerDetails={sharedCustomerDetails}
        />
      )}
      {!!signedAgreements.length && (
        <SidePanelSection heading={t('agreementsSigned')}>
          <EmbeddedCustomerAgreements agreements={agreementsData} />
        </SidePanelSection>
      )}
    </>
  )

  const refetchSummary = () => {
    if (isErrorFetchingAgreements) refetchAgreements()
    if (isErrorFetchingCustomerData) refetchCustomerData()
  }

  const handleRefetchDocuments = () => {
    if (isErrorDocuments) refetchDocuments()
  }

  const summaryTabContent = () => {
    if (isFetchingAgreements || isFetchingCustomerData) {
      return <RowsSkeletonLoader rows={10} />
    }
    if (isErrorFetchingAgreements || isErrorFetchingCustomerData) {
      return (
        <Box paddingX={16} paddingY={32}>
          <RequestErrorStatusCard onReload={refetchSummary} />
        </Box>
      )
    }
    return showSummaryDetails()
  }

  const verificationFailureReasons: VerificationFailure[] =
    infoRequiredData?.verificationFailure?.reasons ?? []

  const documentsTabContent = () => {
    if (isLoadingDocuments) {
      return <RowsSkeletonLoader rows={10} />
    }
    if (isErrorDocuments) {
      return (
        <Box paddingX={16} paddingY={32}>
          <RequestErrorStatusCard onReload={handleRefetchDocuments} />
        </Box>
      )
    }
    return (
      <CustomerDocuments
        documents={documents?.content}
        isCompany={isCompany}
        infoRequiredDocuments={infoRequiredDocuments}
        verificationFailure={verificationFailureReasons}
      />
    )
  }

  return (
    <SidePanel
      id="side-panel-embedded-customer"
      isOpen={isOpen}
      onClose={onClose}
      paging={pageCustomers}
      activeRowId={reference}
      onActiveRowChange={() => {
        highlightRowBasedOnId()
        setActiveTab(tabKeys[0])
      }}
      topOffset={bannerSpacing}
      header={
        <Box flexCol gap={16}>
          <CustomerDetailsHeader
            name={name}
            description={customerDetails?.description}
            StatusHighlight={<EmbeddedCustomerStatusHighlight status={customerDetails?.status} />}
          />
          {showAgreementCTA && (
            <CTASection
              ctaLink={agreementSesstionUrl}
              ctaLabel={t('embeddedCustomers.details.agreementsCtaLabel')}
              helpText={t('embeddedCustomers.details.agreementsCtaPending')}
              isPendingStatus
              isCopyAction
            />
          )}
          {showVerificationCTA && (
            <CTASection
              heading={t('embeddedCustomers.details.verificationLink')}
              ctaLink={verificationUrl}
              ctaLabel={t('embeddedCustomers.details.verificationCtaLabel')}
              helpText={t(
                isVerificationRequired
                  ? 'embeddedCustomers.details.verificationCtaPending'
                  : 'embeddedCustomers.details.verificationCtaComplete'
              )}
              isPendingStatus={isVerificationRequired}
            />
          )}
        </Box>
      }
      body={
        <Box width="full">
          <Box flexCol paddingX={4} paddingB={24} gap={24}>
            <Tabs
              defaultTabId={activeTab}
              onChange={setActiveTab}
              tabs={[
                ...(enableCustomerTimelineTab && isCompany && !isReliance
                  ? [
                      {
                        id: 'Checklist',
                        header: t('embeddedCustomers.tabs.checklist'),
                        content: <CustomerChecklist reference={reference} />,
                        badgeCount: checklistBadgeCount,
                      },
                    ]
                  : []),
                {
                  id: 'Summary',
                  header: t('embeddedCustomers.tabs.summary'),
                  content: summaryTabContent(),
                },

                ...(shouldShowDocumentsTab
                  ? [
                      {
                        id: 'Documents',
                        header: t('embeddedCustomers.tabs.documents'),
                        content: documentsTabContent(),
                      },
                    ]
                  : []),
                ...(showAssociatesTab
                  ? [
                      {
                        id: 'Associates',
                        header: t('embeddedCustomers.tabs.associates'),
                        content: (
                          <AssociateDetails
                            associatesData={associatesData}
                            isLoading={isLoadingAssociates}
                            isError={isErrorAssociates}
                            refetch={refetch}
                            pendingVerificationStatus={!!pendingVerificationStatus}
                          />
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </Box>
        </Box>
      }
    />
  )
}
